import { faBold, faHeading, faImage, faItalic, faLink, faListOl, faListUl, faQuoteRight, faStrikethrough } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import Resizable from 'ui/resizable';

function ToolbarButton({ params, onAction }) {
  const onClick = React.useCallback(() => params.action && onAction(params.action), [params, onAction]);
  if (params.separator) {
    return <li className="d3p-separator"/>;
  }
  return (
    <li className={params.className}>
      <button title={params.title + (params.hotkey ? ` (${params.hotkey})` : "")} onClick={onClick}>
        {params.icon}
      </button>
    </li>
  );
}

export function getSelection(editor) {
  if (!editor) return {start: 0, end: 0, text: ""};
  const start = editor.selectionStart;
  const end = editor.selectionEnd;
  return {start, end, text: editor.value.substring(start, end)};
}

export function replaceSelection(editor, selection, text, offsetStart, offsetEnd) {
  if (!editor) return;
  editor.value = editor.value.substring(0, selection.start) + text + editor.value.substring(selection.end);
  editor.setSelectionRange(selection.start + (offsetStart || 0), selection.start + (offsetEnd == null ? text.length : offsetEnd));
  editor.focus();
}

export function replaceLinePrefix(editor, selection, match, prefix) {
  editor.focus();
  const pos = editor.value.lastIndexOf("\n", selection.start - 1) + 1;
  const line = editor.value.substring(pos);
  const m = line.match(match);
  let delta = 0;
  if (m) {
    editor.value = editor.value.substring(0, pos) + editor.value.substring(pos + m[0].length);
    delta = -m[0].length;
  } else {
    editor.value = editor.value.substring(0, pos) + prefix + editor.value.substring(pos);
    delta = prefix.length;
  }
  editor.setSelectionRange(selection.start + delta, selection.end + delta);
}

export const buttons = {
  separator: {
    separator: true,
  },
  bold: {
    title: "Bold",
    icon: <FontAwesomeIcon icon={faBold}/>,
    action(editor) {
      const sel = getSelection(editor.current);
      replaceSelection(editor.current, sel, `**${sel.text}**`, 2, 2 + sel.text.length);
    },
    hotkey: "Ctrl+B",
  },
  italic: {
    title: "Italic",
    icon: <FontAwesomeIcon icon={faItalic}/>,
    action(editor) {
      const sel = getSelection(editor.current);
      replaceSelection(editor.current, sel, `*${sel.text}*`, 1, 1 + sel.text.length);
    },
    hotkey: "Ctrl+I",
  },
  strikethrough: {
    title: "Strikethrough",
    icon: <FontAwesomeIcon icon={faStrikethrough}/>,
    action(editor) {
      const sel = getSelection(editor.current);
      replaceSelection(editor.current, sel, `~~${sel.text}~~`, 2, 2 + sel.text.length);
    },
  },
  heading: {
    title: "Heading",
    icon: <FontAwesomeIcon icon={faHeading}/>,
    action(editor) {
      const sel = getSelection(editor.current);
      replaceLinePrefix(editor.current, sel, /^#+ /, "## ");
    },
    hotkey: "Ctrl+H",
  },
  quote: {
    title: "Quote",
    icon: <FontAwesomeIcon icon={faQuoteRight}/>,
    action(editor) {
      const sel = getSelection(editor.current);
      replaceLinePrefix(editor.current, sel, /^> /, "> ");
    },
  },
  ul: {
    title: "List",
    icon: <FontAwesomeIcon icon={faListUl}/>,
    action(editor) {
      const sel = getSelection(editor.current);
      replaceLinePrefix(editor.current, sel, /^[*-] /, "* ");
    },
  },
  ol: {
    title: "Numbered List",
    icon: <FontAwesomeIcon icon={faListOl}/>,
    action(editor) {
      const sel = getSelection(editor.current);
      replaceLinePrefix(editor.current, sel, /^\d+[.)] /, "1. ");
    },
  },
  link: {
    title: "Link",
    icon: <FontAwesomeIcon icon={faLink}/>,
    action(editor) {
      const sel = getSelection(editor.current);
      replaceSelection(editor.current, sel, `[${sel.text}](url)`, 1, 1 + sel.text.length);
    },
    hotkey: "Ctrl+K",
  },
  image: {
    title: "Image",
    icon: <FontAwesomeIcon icon={faImage}/>,
    action(editor) {
      const sel = getSelection(editor.current);
      replaceSelection(editor.current, sel, `![${sel.text}](url)`, 2, 2 + sel.text.length);
    },
  },
};

export const defaultToolbar = [buttons.bold, buttons.italic, buttons.strikethrough, buttons.heading, buttons.separator, buttons.quote, buttons.ul, buttons.ol, buttons.separator, buttons.link, buttons.image];

class History {
  actions = [];
  current = 0;

  constructor(state) {
    this.state = state;
  }

  clear() {
    this.actions.length = 0;
    this.current = 0;
  }

  record(text) {
    const prev = this.state;
    this.state = text;
    let len = Math.min(prev.length, text.length);
    let prefix = 0;
    while (prefix < len && prev[prefix] === text[prefix]) {
      prefix += 1;
    }
    if (prefix >= len) return;
    len -= prefix;
    let suffix = 0;
    while (suffix < len && prev[prev.length - suffix - 1] === text[text.length - suffix - 1]) {
      suffix += 1;
    }
    const removed = prev.substring(prefix, prev.length - suffix);
    const inserted = text.substring(prefix, text.length - suffix);

    if (this.actions.length && this.current === this.actions.length) {
      const last = this.actions[this.current - 1];
      if (!last.removed && !removed && prefix === last.pos + last.inserted.length) {
        last.inserted += inserted;
        return;
      }
      if (!last.inserted && !inserted) {
        if (last.pos === prefix + removed.length) {
          last.pos = prefix;
          last.removed = removed + last.removed;
          return;
        }
        if (last.pos === prefix) {
          last.removed += removed;
          return;
        }
      }
    }

    this.actions[this.current++] = {pos: prefix, removed, inserted};
    this.actions.length = this.current;
  }

  canUndo() {
    return this.current > 0;
  }
  undo(input) {
    const action = this.actions[--this.current];
    this.state = this.state.substring(0, action.pos) + action.removed + this.state.substring(action.pos + action.inserted.length);
    input.value = this.state;
    input.setSelectionRange(action.pos + (action.inserted.length ? 0 : action.removed.length), action.pos + action.removed.length);
    return this.state;
  }
  canRedo() {
    return this.current < this.actions.length;
  }
  redo(input) {
    const action = this.actions[this.current++];
    this.state = this.state.substring(0, action.pos) + action.inserted + this.state.substring(action.pos + action.removed.length);
    input.value = this.state;
    input.setSelectionRange(action.pos + (action.removed.length ? 0 : action.inserted.length), action.pos + action.inserted.length);
    return this.state;
  }
}

function makeHotkey(hotkey) {
  const parts = hotkey.split("+");
  const key = "Key" + parts.pop();
  const modifiers = parts.map(mod => mod.toLowerCase() + "Key");
  return e => e.code === key && modifiers.every(mod => e[mod]);
}

export default function Editor({ toolbar=defaultToolbar, value, onChange }) {
  const history = React.useRef();
  const editorRef = React.useRef();

  React.useEffect(() => {
    history.current = new History(editorRef.current?.value || "");
    return () => history.current = null;
  }, []);

  React.useEffect(() => {
    if (history.current && value !== history.current.state) {
      history.current.state = value;
      history.current.clear();
    }
  }, [value]);

  const onEdit = React.useCallback(e => {
    history.current?.record(e.target.value);
    onChange(e.target.value);
  }, [onChange]);

  const onAction = React.useCallback(async action => {
    await action(editorRef);
    if (editorRef.current && history.current) {
      history.current.record(editorRef.current.value);
      onChange(editorRef.current.value);
    }
  }, [onChange]);

  const hotkeys = React.useMemo(() => toolbar.filter(btn => btn.hotkey && btn.action)
    .map(btn => ({filter: makeHotkey(btn.hotkey), action: btn.action})), [toolbar]);

  React.useEffect(() => {
    // we have to use raw event listenere here because
    // 1. HistoryChart installs a global listener for Ctrl+Z on document and we need to call stopPropagation in time
    // 2. React doesn't provide `code` in synthetic events so we'd have to check legacy keyCode
    function onKeyDown(e) {
      for (const key of hotkeys) {
        if (key.filter(e)) {
          e.stopPropagation();
          e.preventDefault();
          onAction(key.action);
          return;
        }
      }
      if (e.code === "KeyZ" && e.ctrlKey) {
        e.stopPropagation();
        e.preventDefault();
        if (e.shiftKey) {
          if (history.current?.canRedo()) {
            onChange(history.current.redo(editorRef.current));
          }
        } else {
          if (history.current?.canUndo()) {
            onChange(history.current.undo(editorRef.current));
          }
        }
      }
    }
    const node = editorRef.current;
    node.addEventListener("keydown", onKeyDown);
    return () => node.removeEventListener("keydown", onKeyDown);
  }, [onChange, hotkeys, onAction]);

  return (
    <Resizable className="d3p-MdEditor" side="s">
      <ul className="d3p-toolbar">
        {toolbar.map((btn, index) => <ToolbarButton key={index} params={btn} onAction={onAction}/>)}
      </ul>
      <div className="d3p-editor">
        <textarea value={value} onChange={onEdit} ref={editorRef} spellCheck={false}/>
      </div>
    </Resizable>
  );
}
